import React, { useState, useEffect } from 'react';
import { createUser, updateUser } from '../../services/api';
import Toast from '../Toast/Toast';
import { useToast } from '../../hooks/useToast';
import { useResponsive } from '../../hooks/useResponsive';
import { X, Copy, Key, Eye, Edit, UserPlus, Info, Calendar, Clock, CheckCircle, XCircle } from 'lucide-react';

function UserModal({ user, mode, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    is_active: true
  });
  const { toasts, hideToast, success, error, warning } = useToast();
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (user && (mode === 'view' || mode === 'edit')) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
    }
  }, [user, mode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (mode === 'add') {
        await createUser(formData);
        success('User created successfully');
      } else if (mode === 'edit') {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if empty
        }
        await updateUser(user.id, updateData);
        success('User updated successfully');
      }
      onSave();
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      error(err.response?.data?.error || 'Operation failed');
    }
  };

  const handleResetPassword = async (newPassword) => {
    try {
      await updateUser(user.id, { 
        ...formData, 
        password: newPassword 
      });
      success(`Password reset successfully! New Password: ${newPassword}`, 5000);
      onSave();
    } catch (err) {
      error('Failed to reset password: ' + (err.response?.data?.error || err.message));
    }
  };

  const isReadOnly = mode === 'view';
  const title = mode === 'add' ? 'Add New User' : mode === 'edit' ? 'Edit User' : 'User Details';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(44, 62, 80, 0.85)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: isMobile ? '16px' : '20px',
      overflowY: 'auto'
    }} onClick={onClose}>
      <div 
        className="card" 
        style={{ 
          width: '100%',
          maxWidth: isMobile ? '100%' : '540px',
          margin: 0,
          position: 'relative',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          padding: isMobile ? '20px' : '28px',
          maxHeight: isMobile ? '95vh' : 'auto',
          overflowY: isMobile ? 'auto' : 'visible'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: isMobile ? '24px' : '32px',
          paddingBottom: isMobile ? '16px' : '20px',
          borderBottom: '2px solid #e8ecf1',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '12px', flex: 1 }}>
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: isMobile ? '8px' : '10px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {mode === 'view' ? <Eye size={isMobile ? 18 : 20} color="white" /> : mode === 'edit' ? <Edit size={isMobile ? 18 : 20} color="white" /> : <UserPlus size={isMobile ? 18 : 20} color="white" />}
            </div>
            <h2 style={{ 
              margin: 0, 
              fontSize: isMobile ? '18px' : '24px', 
              fontWeight: '700', 
              color: '#2c3e50' 
            }}>{title}</h2>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              border: '2px solid #e8ecf1',
              cursor: 'pointer',
              color: '#495057',
              padding: isMobile ? '6px' : '8px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '10px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)';
              e.currentTarget.style.color = '#c62828';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
              e.currentTarget.style.color = '#495057';
            }}
          >
            <X size={isMobile ? 18 : 20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* User ID (view only) */}
          {user && mode !== 'add' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>User ID</label>
              <input
                type="text"
                value={`#${user.id}`}
                readOnly
                style={{ 
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
                  cursor: 'not-allowed',
                  fontWeight: '600',
                  color: '#667eea',
                  fontSize: isMobile ? '14px' : '15px',
                  padding: isMobile ? '10px' : '12px'
                }}
              />
            </div>
          )}

          {/* Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              readOnly={isReadOnly}
              required={!isReadOnly}
              style={isReadOnly ? { 
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
                cursor: 'not-allowed',
                fontSize: isMobile ? '14px' : '15px',
                padding: isMobile ? '10px' : '12px'
              } : {
                fontSize: isMobile ? '14px' : '15px',
                padding: isMobile ? '10px' : '12px'
              }}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Email</label>
            <div style={{ 
              display: 'flex', 
              gap: '10px',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                readOnly={isReadOnly}
                required={!isReadOnly}
                style={isReadOnly ? { 
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
                  cursor: 'not-allowed', 
                  flex: 1, 
                  marginBottom: 0,
                  fontSize: isMobile ? '14px' : '15px',
                  padding: isMobile ? '10px' : '12px'
                } : { 
                  flex: 1, 
                  marginBottom: 0,
                  fontSize: isMobile ? '14px' : '15px',
                  padding: isMobile ? '10px' : '12px'
                }}
              />
              {isReadOnly && (
                <button
                  type="button"
                  className="btn"
                  style={{ 
                    padding: '10px 18px',
                    fontSize: '14px',
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                    color: '#1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: '600',
                    border: 'none'
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(formData.email);
                    success('Email copied to clipboard!');
                  }}
                >
                  <Copy size={16} />
                  <span>Copy</span>
                </button>
              )}
            </div>
          </div>

          {/* Password */}
          {mode === 'view' ? (
            <div style={{ marginBottom: '20px' }}>
              <label>Password</label>
              <div style={{ 
                padding: '16px',
                background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                border: '2px solid #66bb6a',
                borderRadius: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: '700',
                    color: '#2e7d32',
                    fontFamily: 'monospace',
                    letterSpacing: '1.5px'
                  }}>
                    {user?.plain_password || 'Not available'}
                  </div>
                  {user?.plain_password && (
                    <div style={{ fontSize: '13px', color: '#558b2f', marginTop: '6px', fontWeight: '500' }}>
                      Click to copy password
                    </div>
                  )}
                </div>
                {user?.plain_password && (
                  <button
                    type="button"
                    className="btn"
                    style={{ 
                      padding: '10px 18px',
                      fontSize: '14px',
                      background: 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)',
                      color: 'white',
                      marginLeft: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontWeight: '600',
                      border: 'none'
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(user.plain_password);
                      success('Password copied to clipboard!');
                    }}
                  >
                    <Copy size={16} />
                    <span>Copy</span>
                  </button>
                )}
              </div>
              <button
                type="button"
                className="btn"
                style={{ 
                  width: '100%',
                  marginTop: '12px',
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  fontWeight: '600',
                  border: 'none'
                }}
                onClick={() => {
                  const newPassword = prompt('Enter new password for this user:');
                  if (newPassword && newPassword.length >= 6) {
                    handleResetPassword(newPassword);
                  } else if (newPassword) {
                    warning('Password must be at least 6 characters long');
                  }
                }}
              >
                <Key size={18} />
                <span>Reset Password</span>
              </button>
            </div>
          ) : (
            <div style={{ marginBottom: '20px' }}>
              <label>Password {mode === 'edit' && '(leave blank to keep current)'}</label>
              <input
                type="text"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required={mode === 'add'}
                placeholder={mode === 'edit' ? 'Enter new password to change' : 'Enter password'}
              />
              {mode === 'add' && (
                <p style={{ fontSize: '13px', color: '#7f8c8d', marginTop: '8px', marginBottom: 0, fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Info size={16} color="#667eea" />
                  <span>Password will be visible to admin for user management</span>
                </p>
              )}
            </div>
          )}

          {/* Status */}
          <div style={{ marginBottom: '28px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              cursor: isReadOnly ? 'not-allowed' : 'pointer',
              padding: '14px',
              background: formData.is_active ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' : 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
              borderRadius: '12px',
              border: formData.is_active ? '2px solid #66bb6a' : '2px solid #ef5350',
              transition: 'all 0.3s'
            }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                disabled={isReadOnly}
                style={{ width: 'auto', margin: 0, cursor: isReadOnly ? 'not-allowed' : 'pointer' }}
              />
              <span style={{ 
                fontWeight: '600', 
                color: formData.is_active ? '#2e7d32' : '#c62828',
                fontSize: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {formData.is_active ? (
                  <>
                    <CheckCircle size={18} />
                    <span>Active User</span>
                  </>
                ) : (
                  <>
                    <XCircle size={18} />
                    <span>Inactive User</span>
                  </>
                )}
              </span>
            </label>
          </div>

          {/* Created/Updated dates (view only) */}
          {user && mode !== 'add' && (
            <div style={{ 
              marginBottom: '28px',
              padding: '16px',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              borderRadius: '12px',
              fontSize: '14px',
              color: '#495057',
              border: '1px solid #e8ecf1'
            }}>
              <div style={{ marginBottom: '8px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={16} color="#667eea" />
                <span>Created: {new Date(user.created_at).toLocaleString()}</span>
              </div>
              {user.updated_at && (
                <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Clock size={16} color="#667eea" />
                  <span>Updated: {new Date(user.updated_at).toLocaleString()}</span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px' }}>
            {mode !== 'view' && (
              <button 
                type="submit" 
                className="btn btn-primary"
                style={{ flex: 1, fontWeight: '600', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <CheckCircle size={18} />
                <span>{mode === 'add' ? 'Create User' : 'Update User'}</span>
              </button>
            )}
            <button 
              type="button"
              className="btn"
              style={{ 
                flex: 1, 
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                color: '#495057',
                fontWeight: '600',
                fontSize: '15px',
                border: '2px solid #e8ecf1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onClick={onClose}
            >
              <X size={18} />
              <span>{mode === 'view' ? 'Close' : 'Cancel'}</span>
            </button>
          </div>
        </form>

        {/* Toast Notifications */}
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default UserModal;
